import React, { useEffect, useState } from "react";
import { X, ArrowRight, Check } from "lucide-react";
import { brand } from "../mock/content";
import { Link } from "../lib/LocalizedLink";
import Reveal from "./Reveal";
import EuIcon from "./EuIcon";
import factoryVideo from "../assets/factory.mp4";

const INCENTIVI = [
  ["Parco Agrisolare", "per gli investimenti fotovoltaici nel settore agricolo e agroindustriale;"],
  ["Transizione 5.0", "per progetti di efficientamento energetico e trasformazione dei processi produttivi;"],
  ["Revamping e repowering", "di impianti fotovoltaici esistenti;"],
  ["Bandi e programmi di incentivazione", "che prevedono requisiti o premialità legati alla produzione europea dei moduli fotovoltaici."],
];

export default function EneaRegistroSlot() {
  const [open, setOpen] = useState(false);
  return (
    <section className="bg-[#F5F5F3] text-black py-16 md:py-24" data-testid="enea-registro-slot" id="registro-enea">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center border border-black/10 bg-white overflow-hidden">
            <div className="relative h-64 sm:h-80 lg:h-full min-h-[280px]">
              <video src={factoryVideo} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover bg-black" />
              <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-black" style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}>
                <EuIcon size={13} /> Made in EU
              </span>
            </div>
            <div className="p-7 md:p-10 lg:pr-14">
              <span className="inline-flex items-center gap-2 text-black/60 text-[12px] uppercase tracking-[0.28em]" style={{ fontFamily: "Space Grotesk" }}>
                <EuIcon size={15} style={{ color: brand.yellow }} /> Registro ENEA
              </span>
              <h2 className="mt-4 font-black tracking-tight leading-[1.05]" style={{ fontFamily: "Archivo", fontSize: "clamp(1.6rem,3vw,2.6rem)" }}>
                Moduli fotovoltaici X-CORE nel Registro ENEA
              </h2>
              <p className="mt-4 text-black/60 text-base md:text-lg leading-relaxed" style={{ fontFamily: "Inter" }}>
                Pannelli fotovoltaici europei per Agrisolare, Transizione 5.0, revamping e investimenti incentivati.
              </p>
              <p className="mt-4 text-black/55 text-sm md:text-base leading-relaxed" style={{ fontFamily: "Inter" }}>
                I moduli TORRI X-CORE sono iscritti al Registro ENEA delle tecnologie per il fotovoltaico, che identifica i moduli conformi ai requisiti previsti per le tecnologie prodotte nell'Unione Europea.
              </p>
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="enea-registro-open"
                className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 text-black text-sm uppercase tracking-[0.14em] font-semibold transition-transform hover:scale-[1.03]"
                style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}
              >
                Scopri di più <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
      <EneaRegistroModal open={open} onClose={() => setOpen(false)} />
    </section>
  );
}

export function EneaRegistroModal({ open, onClose }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!open) { setShow(false); return; }
    const raf = requestAnimationFrame(() => setShow(true));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { cancelAnimationFrame(raf); document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[130] overflow-y-auto overscroll-contain" data-testid="enea-registro-modal" role="dialog" aria-modal="true">
      <div className={`fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity duration-300 ${show ? "opacity-100" : "opacity-0"}`} onClick={onClose} />
      <div className="relative min-h-full flex items-center justify-center p-4">
        <div className={`relative w-[min(720px,96vw)] bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden transition-all duration-300 ${show ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-[0.98]"}`}>
          <button onClick={onClose} aria-label="Chiudi" data-testid="enea-registro-close" className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/60 border border-white/20 text-white/70 hover:text-white hover:border-white/50 transition-colors">
            <X size={18} />
          </button>

          <div className="max-h-[88vh] overflow-y-auto">
            <div className="relative h-44 md:h-52">
              <video src={factoryVideo} autoPlay muted loop playsInline className="w-full h-full object-cover bg-black" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/30 to-transparent" />
              <span className="absolute bottom-4 left-6 inline-flex items-center gap-2 text-white/60 text-[12px] uppercase tracking-[0.28em]" style={{ fontFamily: "Space Grotesk" }}>
                <EuIcon size={15} style={{ color: brand.yellow }} /> Registro ENEA
              </span>
            </div>

            <div className="p-6 md:p-9 text-white">
              <h2 className="font-black tracking-tight leading-[1.08]" style={{ fontFamily: "Archivo", fontSize: "clamp(1.6rem,3vw,2.4rem)" }}>
                Moduli fotovoltaici X-CORE nel Registro ENEA
              </h2>
              <p className="mt-3 text-[#F9E212] text-base md:text-lg font-semibold" style={{ fontFamily: "Inter" }}>
                Pannelli fotovoltaici europei per Agrisolare, Transizione 5.0, revamping e investimenti incentivati
              </p>

              <p className="mt-5 text-white/70 leading-relaxed" style={{ fontFamily: "Inter" }}>
                I moduli fotovoltaici TORRI X-CORE sono iscritti al Registro ENEA delle tecnologie per il fotovoltaico, previsto per identificare i moduli che rispondono ai requisiti richiesti dalla normativa per le tecnologie prodotte nell'Unione Europea.
              </p>
              <p className="mt-4 text-white/70 leading-relaxed" style={{ fontFamily: "Inter" }}>
                L'iscrizione al Registro ENEA rende X-CORE una soluzione adatta ai progetti fotovoltaici che, secondo le condizioni previste dalle singole misure, richiedono o valorizzano l'utilizzo di pannelli fotovoltaici Made in EU.
              </p>

              <h3 className="mt-8 font-black text-lg md:text-xl tracking-tight" style={{ fontFamily: "Archivo" }}>Fotovoltaico europeo e accesso agli incentivi</h3>
              <p className="mt-3 text-white/70 leading-relaxed" style={{ fontFamily: "Inter" }}>
                I moduli TORRI X-CORE possono essere impiegati, previa verifica dei requisiti specifici del progetto e della misura applicabile, nell'ambito di interventi quali:
              </p>
              <ul className="mt-4 space-y-3">
                {INCENTIVI.map(([k, v]) => (
                  <li key={k} className="flex items-start gap-3">
                    <Check size={18} className="mt-0.5 shrink-0" style={{ color: brand.yellow }} />
                    <span className="text-white/70 leading-relaxed" style={{ fontFamily: "Inter" }}>
                      <span className="text-white font-semibold">{k}</span>, {v}
                    </span>
                  </li>
                ))}
              </ul>

              <h3 className="mt-8 font-black text-lg md:text-xl tracking-tight" style={{ fontFamily: "Archivo" }}>X-CORE: tecnologia europea, Disegno Italiano</h3>
              <p className="mt-3 text-white/70 leading-relaxed" style={{ fontFamily: "Inter" }}>
                X-CORE nasce dalla cultura di prodotto TORRI: progettare un modulo fotovoltaico capace di offrire sicurezza, robustezza e longevità, andando oltre lo standard di mercato.
              </p>
              <p className="mt-4 text-white/70 leading-relaxed" style={{ fontFamily: "Inter" }}>
                L'iscrizione al Registro ENEA aggiunge un ulteriore elemento: la possibilità di scegliere una tecnologia europea anche per gli investimenti nei quali origine e caratteristiche del modulo fotovoltaico rappresentano un requisito per accedere alle agevolazioni previste.
              </p>

              <div className="mt-8 border-t border-white/10 pt-6">
                <p className="font-black text-xl tracking-tight" style={{ fontFamily: "Archivo" }}>TORRI X-CORE</p>
                <p className="text-white/50 text-sm mt-1" style={{ fontFamily: "Inter" }}>Disegno Italiano. Tecnologia europea.</p>
              </div>

              <Link
                to="/contatti"
                data-testid="enea-registro-cta-xcore"
                onClick={onClose}
                className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 text-black text-sm uppercase tracking-[0.14em] font-semibold transition-transform hover:scale-[1.02]"
                style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}
              >
                Richiedi info <ArrowRight size={16} />
              </Link>

              <p className="mt-5 text-white/60 text-xs leading-relaxed" style={{ fontFamily: "Inter" }}>
                L'impiego dei moduli nelle singole misure è sempre subordinato alla verifica dei requisiti specifici del progetto e della normativa applicabile.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
